import { Component, forwardRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GraphError } from '@microsoft/microsoft-graph-client';
import { OnChangeType, OnTouchedType } from 'ng-zorro-antd/core/types';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { fixPjpegAvatar, IUserProfile, LookupState, trackByRef } from 'src/app/lego-utils';
import { SharePointService } from 'src/app/services/share-point.service';

/**
 * Searches Azure AD for a LEGO colleague and emits the picked {@link IUserProfile}.
 *
 * Requires the app registration to hold (admin-consented) Graph `User.ReadBasic.All`.
 */
@Component({
  selector: 'em-user-search-field',
  styleUrls: ['./user-search-field.component.less'],
  templateUrl: './user-search-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => UserSearchFieldComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class UserSearchFieldComponent implements ControlValueAccessor {
  public readonly trackByRef = trackByRef;
  public readonly fixPjpegAvatar = fixPjpegAvatar;
  public readonly lookupStateEnum = LookupState;

  public foundUsers$: Observable<IUserProfile[]>;
  public userLookupState: LookupState = LookupState.INIT;
  public userLookupErrorMsg?: string;

  @ViewChild('userSelect', { static: true }) private readonly userSelectRef!: NzSelectComponent;
  private readonly userSearchChange$ = new Subject<string>();

  public constructor(private readonly sharePointService: SharePointService) {
    this.foundUsers$ = this.userSearchChange$.pipe(
      debounceTime(500),
      filter((searchValue) => {
        if (searchValue && searchValue.length > 1) {
          return true;
        }

        this.userLookupState = LookupState.INIT;

        return false;
      }),
      tap(() => {
        this.userLookupState = LookupState.LOADING;
        this.userLookupErrorMsg = undefined;
      }),
      switchMap((searchValue) =>
        this.sharePointService.searchUserProfilesWithPhotos(searchValue, 5).pipe(
          tap(() => {
            this.userLookupState = LookupState.RESULT;
          }),
          catchError((error: Error | GraphError) => {
            this.userLookupErrorMsg = error.message;
            this.userLookupState = LookupState.ERROR;

            return of([] as IUserProfile[]);
          })
        )
      )
    );
  }

  public onSearch(searchValue: string): void {
    this.userSearchChange$.next(searchValue);
  }

  public writeValue(modelValue: unknown): void {
    this.userSelectRef.writeValue(modelValue);
  }

  public registerOnChange(fn: OnChangeType): void {
    this.userSelectRef.registerOnChange(fn);
  }

  public registerOnTouched(fn: OnTouchedType): void {
    this.userSelectRef.registerOnTouched(fn);
  }

  public setDisabledState(isDisabled: boolean): void {
    this.userSelectRef.setDisabledState(isDisabled);
  }
}
